import { useState } from 'react';

import {
  faThumbsDown,
  faThumbsUp,
} from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import {
  faThumbsDown as faThumbsDownSolid,
  faThumbsUp as faThumbsUpSolid,
} from '@awesome.me/kit-f3dc60f0d0/icons/classic/solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLingui } from '@lingui/react/macro';
import { Toast } from '@repo/videa-components/src/index';
import { Button } from '@repo/videa-components/src/ui/button';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';

import { FeedbackDialog } from './FeedbackDialog';
import { InlineFeedbackBox } from './InlineFeedbackBox';
import type { FeedbackOption, MiddleAction } from './types';

interface FeedbackWidgetProps {
  positiveOptions: FeedbackOption[];
  negativeOptions: FeedbackOption[];
  label?: React.ReactNode;
  className?: string;
  inlineFeedbackBoxClassName?: string;
  variant?: 'default' | 'minimalist';
  middleAction?: MiddleAction;
  onRate: (
    rating: 'positive' | 'negative',
    selectedOptions: string[],
    additionalComments?: string,
  ) => void;
  onRatingButtonClick?: (rating: 'positive' | 'negative') => void;
  // By default, the inline box will appear directly under the widget
  // This prop is optional for cases where we want it to appear in a different location
  inlineFeedbackBoxContainerRef?: React.RefObject<HTMLDivElement>;
}

export const FeedbackWidget = ({
  className = '',
  inlineFeedbackBoxClassName = '',
  label = '',
  positiveOptions,
  negativeOptions,
  variant = 'default',
  middleAction,
  onRate,
  onRatingButtonClick,
  inlineFeedbackBoxContainerRef,
}: FeedbackWidgetProps) => {
  // state
  const [rating, setRating] = useState<'positive' | 'negative' | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [showInlineFeedback, setShowInlineFeedback] = useState(false);
  const [selectedFeedbackOptions, setSelectedFeedbackOptions] = useState<
    string[]
  >([]);

  // hooks
  const { t } = useLingui();

  // handlers
  const handleRate = (newRating: 'positive' | 'negative') => {
    onRatingButtonClick?.(newRating);
    setRating(newRating);

    if (newRating === 'positive') {
      // For positive ratings, immediately submit and show toast
      onRate(newRating, []);
      toast(
        <Toast
          variant="success"
          title={t`Feedback sent!`}
          description={t`Thanks for helping us improve the product.`}
        />,
        {
          closeButton: true,
          position: 'top-right',
          style: { width: '460px' },
        },
      );
    } else {
      // For negative ratings, show the feedback box
      setShowInlineFeedback(true);
      setSelectedFeedbackOptions([]);
    }
  };

  const handleDialogOpenChange = (open: boolean) => {
    setIsDialogOpen(open);
    if (!open) {
      setRating(null);
      setShowInlineFeedback(false);
      setSelectedFeedbackOptions([]);
    }
  };

  const handleInlineFeedbackClose = () => {
    setShowInlineFeedback(false);
    setRating(null);
    setSelectedFeedbackOptions([]);
  };

  const handleToggleOption = (value: string) => {
    if (selectedFeedbackOptions.includes(value)) {
      setSelectedFeedbackOptions(
        selectedFeedbackOptions.filter((item) => item !== value),
      );
    } else {
      setSelectedFeedbackOptions([...selectedFeedbackOptions, value]);
    }
  };

  const handleMoreClick = () => {
    setIsDialogOpen(true);
  };

  const handleSubmitInlineFeedback = () => {
    if (!rating) return;

    onRate(rating, selectedFeedbackOptions);
    setShowInlineFeedback(false);
    setSelectedFeedbackOptions([]);

    // Show success toast
    toast(
      <Toast
        variant="success"
        title={t`Feedback sent!`}
        description={t`Thanks for helping us improve the product.`}
      />,
      {
        closeButton: true,
        position: 'top-right',
        style: { width: '460px' },
      },
    );
  };

  return (
    <div className={`vai-flex vai-flex-col ${className}`}>
      <div className="vai-flex vai-items-center vai-gap-2">
        {/* Label */}
        {label && (
          <span className="vai-text-sm vai-text-gray-700">{label}</span>
        )}
        <div className={`vai-flex ${variant === 'default' ? 'vai-gap-1' : ''}`}>
          {/* Positive button */}
          <Button
            variant={variant === 'minimalist' ? 'ghost' : 'outline'}
            size={variant === 'minimalist' ? 'sm' : 'default'}
            title={t`Rate positively`}
            onClick={() => handleRate('positive')}
            data-testid="feedback-positive-button"
            className={
              variant === 'minimalist' ? 'vai-max-w-8 vai-max-h-8' : ''
            }
          >
            <FontAwesomeIcon
              icon={rating === 'positive' ? faThumbsUpSolid : faThumbsUp}
              className="vai-text-muted-foreground"
            />
          </Button>
          {/* Optional middle action button */}
          {middleAction && (
            <Button
              variant="ghost"
              size={variant === 'minimalist' ? 'sm' : 'default'}
              title={middleAction.title}
              disabled={middleAction.isDisabled}
              onClick={middleAction.onClick}
              className={
                variant === 'minimalist' ? 'vai-max-w-8 vai-max-h-8' : ''
              }
            >
              <div className="vai-text-muted-foreground">
                {middleAction.icon}
              </div>
            </Button>
          )}
          {/* Negative button */}
          <Button
            variant={variant === 'minimalist' ? 'ghost' : 'outline'}
            size={variant === 'minimalist' ? 'sm' : 'default'}
            title={t`Rate negatively`}
            onClick={() => handleRate('negative')}
            data-testid="feedback-negative-button"
            className={
              variant === 'minimalist' ? 'vai-max-w-8 vai-max-h-8' : ''
            }
          >
            <FontAwesomeIcon
              icon={rating === 'negative' ? faThumbsDownSolid : faThumbsDown}
              className="vai-text-muted-foreground"
            />
          </Button>
        </div>
      </div>

      {/* Inline feedback box */}
      {showInlineFeedback &&
        rating === 'negative' &&
        (inlineFeedbackBoxContainerRef?.current ? (
          createPortal(
            <InlineFeedbackBox
              rating={rating}
              positiveOptions={positiveOptions}
              negativeOptions={negativeOptions}
              selectedFeedbackOptions={selectedFeedbackOptions}
              className={inlineFeedbackBoxClassName}
              onToggleOption={handleToggleOption}
              onClose={handleInlineFeedbackClose}
              onMoreClick={handleMoreClick}
              onSubmit={handleSubmitInlineFeedback}
            />,
            inlineFeedbackBoxContainerRef.current,
          )
        ) : (
          <InlineFeedbackBox
            rating={rating}
            positiveOptions={positiveOptions}
            negativeOptions={negativeOptions}
            selectedFeedbackOptions={selectedFeedbackOptions}
            className={inlineFeedbackBoxClassName}
            onToggleOption={handleToggleOption}
            onClose={handleInlineFeedbackClose}
            onMoreClick={handleMoreClick}
            onSubmit={handleSubmitInlineFeedback}
          />
        ))}

      {/* Feedback dialog */}
      <FeedbackDialog
        initialSelectedOptions={selectedFeedbackOptions}
        rating={rating}
        positiveOptions={positiveOptions}
        negativeOptions={negativeOptions}
        isOpen={isDialogOpen}
        onRate={onRate}
        onOpenChange={handleDialogOpenChange}
      />
    </div>
  );
};
